/**
 * Shared presentation for the collateral pages: Property Valuations, Title
 * Searches, Lien Verifications, UCC Filings and the property dossier. The
 * page header (ListHeader) is also the header of the Lien Filings and Private
 * Equity pages, so the whole group reflows one way at phone width (UI run r3,
 * fi-g5-4). Their tile rows are the kit's MetricCardGrid (fi-cons-13).
 *
 * One absent marker, one label map per spec enum, one currency rule and one
 * treatment for a tile whose read is pending, failed or unreported. Every
 * piece composes @aaimplatform/ui primitives; nothing here draws its own
 * badge, empty state or banner.
 *
 * An absent figure is never shown as a measured 0 (.claude/rules/
 * no-fabricated-values.md). The 2026-09-27 lien ruling is the case in point:
 * a title search without lien data reports lienCount, totalLienAmount,
 * riskLevel and titleRiskLevel as null, and a zero in their place asserts a
 * clean title.
 */

import type { ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  METRIC_READ_CAPTIONS,
  Skeleton,
  VisuallyHidden,
  type BadgeProps,
} from '@aaimplatform/ui';
import type { components as PropertyTitleComponents } from '@aaimplatform/institutional-types/property-title-admin';
import type { components as UccLifecycleComponents } from '@aaimplatform/institutional-types/ucc-filing-lifecycle';

type ValuationStatus = PropertyTitleComponents['schemas']['ValuationStatus'];
type TitleSearchStatus = PropertyTitleComponents['schemas']['TitleSearchStatus'];
type TitleRiskLevel = PropertyTitleComponents['schemas']['TitleRiskLevel'];
type LienRiskLevel = PropertyTitleComponents['schemas']['LienRiskLevel'];
type TaxLienRisk = PropertyTitleComponents['schemas']['TaxLienRisk'];
type FilingStatus = UccLifecycleComponents['schemas']['FilingStatus'];
type FilingType = UccLifecycleComponents['schemas']['FilingType'];

// ---------------------------------------------------------------------------
// Absent marker
// ---------------------------------------------------------------------------

/** An absent value: an em dash on screen, "not reported" to a screen reader. */
export function Absent() {
  return (
    <>
      <span aria-hidden="true" className="text-quaternary">—</span>
      <VisuallyHidden>not reported</VisuallyHidden>
    </>
  );
}

// ---------------------------------------------------------------------------
// Figures
// ---------------------------------------------------------------------------

/**
 * A reported number, or null when the field is absent. Accepts the string
 * form the property-title-admin service serializes a BigInt amount as, so
 * `Number(v) || 0` never turns an absent amount into a measured $0.
 */
export function reportedNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** The sum of the reported figures, or null when no row reports one. */
export function sumReported(values: ReadonlyArray<number | null>): number | null {
  const reported = values.filter((v): v is number => v != null);
  return reported.length > 0 ? reported.reduce((sum, v) => sum + v, 0) : null;
}

/** The mean of the reported figures, or null when no row reports one. */
export function meanReported(values: ReadonlyArray<number | null>): number | null {
  const reported = values.filter((v): v is number => v != null);
  return reported.length > 0 ? reported.reduce((sum, v) => sum + v, 0) / reported.length : null;
}

/**
 * The kit's dollar formatters: formatUsdCompact on a tile ('$2.4M'), formatUsd
 * in a table cell ('$2,449,975.00'). This module printed whole dollars on both,
 * so one book read three ways across the portal (UI run r3, fi-cons-1). A
 * reported 0 formats as '$0' on a tile and '$0.00' in a cell.
 */
export { formatUsd, formatUsdCompact } from '@aaimplatform/ui';

// ---------------------------------------------------------------------------
// Metric tiles
// ---------------------------------------------------------------------------

/**
 * The value and caption for a MetricCard. A tile whose read has not returned,
 * failed, or has no reported figure prints `--` with the kit caption saying
 * why; it never prints the 0 an empty array reduces to.
 */
/** Where a list read stands (UI state, not an API shape). */
export type ListReadState = 'loading' | 'failed' | 'loaded';

export function tile(
  read: ListReadState,
  value: string | null,
  absentCaption: string = METRIC_READ_CAPTIONS.notReported,
): { value: string; caption?: string } {
  if (read === 'loading') return { value: '--', caption: METRIC_READ_CAPTIONS.unread };
  if (read === 'failed') return { value: '--', caption: METRIC_READ_CAPTIONS.failed };
  if (value == null) return { value: '--', caption: absentCaption };
  return { value };
}

// ---------------------------------------------------------------------------
// Enum labels
// ---------------------------------------------------------------------------

/**
 * The one map from a status kind to the kit Badge variant, color for color.
 * Badge is the portal's one status chip (UI run r3, status-chip-primitive): it
 * prints the registered label as written, where the kit StatusBadge upper-cased
 * whatever it was handed, so 'Needs review' read 'NEEDS REVIEW' here and
 * 'Active' in sentence case on the pledge pages (fi-cons-8). A page whose tone
 * helper still returns a kind (the Tempo delivery-mode helper) renders through
 * this map. No kind maps to 'info'.
 */
export const BADGE_TONE = {
  active: 'success',
  pending: 'warning',
  warning: 'warning',
  error: 'error',
  inactive: 'neutral',
} as const satisfies Record<string, NonNullable<BadgeProps['variant']>>;

/** A status kind and its label; the kind set is BADGE_TONE's keys. */
export function badge(kind: keyof typeof BADGE_TONE, label: string) {
  return { kind, label };
}

export const VALUATION_STATUS: Record<ValuationStatus, ReturnType<typeof badge>> = {
  CERTIFIED: badge('active', 'Certified'),
  PENDING: badge('pending', 'Pending'),
  NEEDS_REVIEW: badge('warning', 'Needs review'),
  FAILED: badge('error', 'Failed'),
};

export const TITLE_SEARCH_STATUS: Record<TitleSearchStatus, ReturnType<typeof badge>> = {
  CLEAR: badge('active', 'Clear'),
  COMPLETE: badge('active', 'Complete'),
  IN_PROGRESS: badge('pending', 'In progress'),
  ISSUES_FOUND: badge('error', 'Issues found'),
  FAILED: badge('error', 'Failed'),
};

export const TITLE_RISK_LEVEL: Record<TitleRiskLevel, ReturnType<typeof badge>> = {
  LOW: badge('active', 'Low'),
  MEDIUM: badge('warning', 'Medium'),
  HIGH: badge('error', 'High'),
  CRITICAL: badge('error', 'Critical'),
};

export const LIEN_RISK_LEVEL: Record<LienRiskLevel, ReturnType<typeof badge>> = TITLE_RISK_LEVEL;

export const FILING_STATUS: Record<FilingStatus, ReturnType<typeof badge>> = {
  draft: badge('pending', 'Draft'),
  submitted: badge('pending', 'Submitted'),
  rejected: badge('error', 'Rejected'),
  acknowledged: badge('active', 'Acknowledged'),
  active: badge('active', 'Active'),
  amended: badge('pending', 'Amended'),
  continued: badge('active', 'Continued'),
  terminated: badge('inactive', 'Terminated'),
  lapsed: badge('error', 'Lapsed'),
  imported: badge('pending', 'Imported'),
};

export const FILING_TYPE_LABELS: Record<FilingType, string> = {
  ucc1: 'UCC-1',
  ucc3Amendment: 'UCC-3 Amendment',
  ucc3Continuation: 'UCC-3 Continuation',
  ucc3Termination: 'UCC-3 Termination',
};

/**
 * LienVerification.taxLienRisk is the nullable TaxLienRisk enum (op-32). Null is
 * an absent assessment and renders as Absent; the enum has no UNKNOWN member.
 */
export const TAX_LIEN_RISK_LABELS: Record<TaxLienRisk, string> = {
  NONE: 'None',
  LOW: 'Low',
  ELEVATED: 'Elevated',
  CRITICAL: 'Critical',
};

/**
 * PropertyValuation.propertyType is a free string: the Prisma PropertyType
 * enum on a stored row, or the lower-case value the New Valuation form sends.
 */
export const PROPERTY_TYPE_LABELS: Record<string, string> = {
  SINGLE_FAMILY: 'Single-family',
  MULTI_FAMILY: 'Multifamily',
  CONDO: 'Condominium',
  TOWNHOUSE: 'Townhouse',
  COMMERCIAL: 'Commercial',
  LAND: 'Land',
  residential: 'Residential',
  commercial: 'Commercial',
  industrial: 'Industrial',
  land: 'Land',
  mixed_use: 'Mixed use',
};

/**
 * PropertyValuation.propertyType values that carry no type. The list mapping
 * in platform-admin-api substitutes 'UNKNOWN' when characteristics arrive
 * without one; that is an absence, not a type.
 */
const PROPERTY_TYPE_ABSENT = new Set(['', 'UNKNOWN']);

export function reportedPropertyType(v: string | null | undefined): string | null {
  if (v == null || PROPERTY_TYPE_ABSENT.has(v)) return null;
  return v;
}

/** Sentence case for a code no label map carries yet: SOME_CODE -> Some code. */
export function humanizeCode(code: string): string {
  const words = code
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A label from the map, falling back to sentence case, never the raw code. */
export function labelFor(map: Record<string, string>, code: string): string {
  return map[code] ?? humanizeCode(code);
}

/** An enum value as a kit Badge through its label map, or Absent when null. */
export function EnumBadge({
  value,
  map,
}: {
  value: string | null | undefined;
  map: Record<string, ReturnType<typeof badge>>;
}) {
  if (value == null || value === '') return <Absent />;
  const spec = map[value] ?? badge('inactive', humanizeCode(value));
  return <Badge variant={BADGE_TONE[spec.kind]}>{spec.label}</Badge>;
}

/** The select options a FilterBar offers for a label map. */
export function filterOptions(map: Record<string, ReturnType<typeof badge>>): Array<{ value: string; label: string }> {
  return Object.entries(map).map(([value, spec]) => ({ value, label: spec.label }));
}

// ---------------------------------------------------------------------------
// Page chrome
// ---------------------------------------------------------------------------

/**
 * The subtitle and action row every collateral, lien and private-equity list
 * page opens with. The actions sit in a wrapping row, so on a phone each
 * button keeps its own width rather than stretching across the column.
 *
 * `refreshing` spins the icon and disables the button while a refetch is in
 * flight, so a second press cannot queue behind the first.
 */
export function ListHeader({
  subtitle,
  onRefresh,
  refreshing = false,
  children,
}: {
  subtitle: ReactNode;
  onRefresh: () => void;
  refreshing?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
      <div>
        {/* The page title lives in the sticky topnav (AppHeader); the body
            opens with the descriptive subtitle, no redundant H1. */}
        {subtitle}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw
            className={refreshing ? 'w-4 h-4 animate-spin' : 'w-4 h-4'}
            aria-hidden="true"
          />
          Refresh
        </Button>
        {children}
      </div>
    </div>
  );
}

/** Placeholder for the grid while the list read is in flight. */
export function GridSkeleton() {
  return (
    <div className="bg-surface rounded-b-sm shadow-sm p-8" role="status" aria-label="Loading">
      <Skeleton className="h-8 w-full mb-4" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full mb-2" />
      ))}
    </div>
  );
}

/** The empty grid: EmptyState on the grid's surface, below the FilterBar. */
export function GridEmpty(props: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="bg-surface rounded-b-sm shadow-sm">
      <EmptyState {...props} />
    </div>
  );
}
